// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Identifier system used to mark the tenant Organization that represents a
 * Polaris environment (dev-<handle>, e2e, preview-<pr>, staging, production)
 * within the shared Medplum Project. Every environment-scoped resource is
 * tagged with { system: POLARIS_ENV_TAG_SYSTEM, code: <env> }, and the env's
 * Organization carries a matching identifier.
 */
export const POLARIS_ENV_TAG_SYSTEM = 'https://polarissleep.com/env';

export type PolarisEnv = string;

const ENV_PATTERN = /^(dev-[a-z0-9][a-z0-9-]*|e2e|preview-[a-z0-9][a-z0-9-]*|staging|production)$/;

/**
 * Validates that the given string is a well-formed Polaris environment name.
 *
 * Accepted values:
 * - `dev-<handle>` — a developer's local environment (e.g. `dev-hugo`)
 * - `e2e` — the shared end-to-end test environment
 * - `preview-<pr>` — a Vercel preview build (e.g. `preview-1234`)
 * - `staging` — the staging environment
 * - `production` — the production environment
 */
export function isValidPolarisEnv(value: string | undefined | null): value is PolarisEnv {
  return typeof value === 'string' && ENV_PATTERN.test(value);
}

/**
 * Parses and validates a Polaris environment name. Throws if invalid.
 */
export function parsePolarisEnv(value: string | undefined | null): PolarisEnv {
  if (!isValidPolarisEnv(value)) {
    throw new Error(
      `Invalid POLARIS_ENV: "${value ?? ''}". Expected one of: dev-<handle>, e2e, preview-<pr>, staging, production.`
    );
  }
  return value;
}

/** Returns true for a per-branch `preview-<slug>` environment. */
export function isPreviewEnv(env: PolarisEnv): boolean {
  return env.startsWith('preview-');
}

/**
 * Returns the deterministic FHIR identifier used to locate the tenant
 * Organization for a given Polaris environment.
 */
export function getEnvOrgIdentifier(env: PolarisEnv): { system: string; value: string } {
  return { system: POLARIS_ENV_TAG_SYSTEM, value: env };
}

/**
 * Returns the `<env>__` prefix applied to every Bot name deployed into a given
 * Polaris environment, so that one non-production Medplum Project can safely
 * host the bots of many environments without collisions.
 *
 * **Production is special-cased to return an empty string.** Production runs
 * in its own dedicated Medplum Project with exactly one environment (itself),
 * so the prefix would only clutter Bot names. Every other env (dev-*, e2e,
 * preview-*, staging) shares a single non-prod Project and needs the prefix
 * to coexist.
 */
export function getEnvBotNamePrefix(env: PolarisEnv): string {
  if (env === 'production') {
    return '';
  }
  return `${env}__`;
}

/**
 * Maximum length of the slug portion of a `preview-<slug>` env name. Keeps
 * generated Bot names well below Medplum's resource field limits even with
 * the `<env>__<botName>` prefix applied.
 */
export const PREVIEW_ENV_SLUG_MAX_LENGTH = 32;

/**
 * Length of the deterministic hash suffix appended to over-long branch slugs.
 * 8 hex chars (32 bits) is enough to make collisions between stacked branches
 * negligible while keeping the env name short and human-skimmable.
 */
export const PREVIEW_ENV_HASH_LENGTH = 8;

// 32-bit FNV-1a. Pure JS so this module stays browser-safe — `node:crypto`
// can't be bundled into the patient-app / provider-app Vite builds, which both
// import `parsePolarisEnv` from this file at app boot.
function fnv1a32Hex(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Converts a Git branch name into a valid `preview-<slug>` Polaris env name.
 *
 * Branch names accept characters (uppercase, slashes, dots, underscores) that
 * are not valid in an env name, so this collapses everything to lowercase
 * alphanumerics joined by single hyphens, then either:
 *
 * - returns the normalized slug as-is when it already fits within
 *   `PREVIEW_ENV_SLUG_MAX_LENGTH`, or
 * - returns a `<prefix>-<hash>` form where `<hash>` is a deterministic 8-hex
 *   FNV-1a hash of the original branch name. Without the hash, stacked
 *   branches whose suffix carries the only discriminator (e.g.
 *   `plan/foo/phase-3` vs `plan/foo/phase-4`) would collapse to the same
 *   truncated prefix and share a tenant Organization + bot prefix.
 *
 * @throws if the normalized slug is empty (e.g. the branch name was all punctuation).
 */
export function slugifyBranchToPreviewEnv(branch: string): PolarisEnv {
  const normalized = branch
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!normalized) {
    throw new Error(`Cannot derive a preview env from branch name "${branch}" — resulting slug is empty.`);
  }

  if (normalized.length <= PREVIEW_ENV_SLUG_MAX_LENGTH) {
    return parsePolarisEnv(`preview-${normalized}`);
  }

  // Hash the raw branch name (not the normalized slug) so two branches that
  // normalize to the same prefix still get distinct hashes.
  const hash = fnv1a32Hex(branch);
  const prefixBudget = PREVIEW_ENV_SLUG_MAX_LENGTH - 1 - PREVIEW_ENV_HASH_LENGTH;
  const prefix = normalized.slice(0, prefixBudget).replace(/-+$/g, '');
  return parsePolarisEnv(`preview-${prefix}-${hash}`);
}

/**
 * Builds a fully-qualified bot name for an environment. In production this
 * returns the bare bot name (see `getEnvBotNamePrefix`).
 */
export function getEnvBotName(env: PolarisEnv, botName: string): string {
  return `${getEnvBotNamePrefix(env)}${botName}`;
}
